import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { VendorAssessmentDialog } from '@/components/compliance/VendorAssessmentDialog'
import { ChevronRight } from 'lucide-react'
import { PageHeaderBreadcrumb } from '@/components/layout/PageHeaderActions'
import { usePageHeaderTitle } from '@/lib/page-header'
import { trpc } from '@/lib/trpc'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/vendors_/$vendorId_/assessments/$assessmentId'
)({
  component: VendorAssessmentPage,
  staticData: {
    pageHeader: { title: 'Vendor Assessment', pad: false, scroll: false },
  },
})

function VendorAssessmentPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { workspaceId, vendorId, assessmentId } = Route.useParams()

  const assessmentQuery = useQuery({
    ...trpc.vendorAssessment.getById.queryOptions({ id: assessmentId }),
    enabled: Boolean(assessmentId),
  })
  const assessment = assessmentQuery.data

  usePageHeaderTitle(
    assessment ? `${assessment.vendorDisplayId}-ASM-${assessment.version}: ${assessment.vendorName} assessment` : 'Vendor Assessment'
  )

  const goBackToVendors = () => {
    navigate({
      to: '/workspaces/$workspaceId/vendors',
      params: { workspaceId },
    })
  }

  const goBackToVendor = () => {
    navigate({
      to: '/workspaces/$workspaceId/vendors/$vendorId',
      params: { workspaceId, vendorId },
    })
  }

  return (
    <div className='flex h-[calc(var(--app-vh)_-_50px)] flex-col overflow-hidden'>
      {/* Full trail: Vendors › <vendor> › Assessment vN (the shell renders
          the last separator before the title). */}
      <PageHeaderBreadcrumb>
        <button
          type='button'
          onClick={goBackToVendors}
          className='font-heading text-[15px] font-medium text-text-subtle hover:text-cobalt-strong'
        >
          Vendors
        </button>
        <ChevronRight
          className='text-text-placeholder h-3.5 w-3.5 shrink-0'
          aria-hidden
        />
        <button
          type='button'
          onClick={goBackToVendor}
          className='font-heading text-[15px] font-medium text-text-subtle hover:text-cobalt-strong'
        >
          {assessment?.vendorName ?? 'Vendor'}
        </button>
      </PageHeaderBreadcrumb>
      <VendorAssessmentDialog
        mode='page'
        assessmentId={assessmentId}
        open
        onOpenChange={(v) => {
          if (!v) goBackToVendor()
        }}
        onUpdate={() => {
          for (const queryKey of [
            trpc.vendorManagement.getDistribution.queryKey(),
            trpc.vendorManagement.getStats.queryKey(),
            trpc.vendorManagement.getBaaStats.queryKey(),
            trpc.vendorManagement.list.queryKey(),
            trpc.vendorManagement.getById.queryKey({ id: vendorId }),
          ]) queryClient.invalidateQueries({ queryKey })
          queryClient.invalidateQueries({
            queryKey: trpc.vendorAssessment.getById.queryKey({
              id: assessmentId,
            }),
          })
          queryClient.invalidateQueries({
            queryKey: trpc.vendorAssessment.listByVendor.queryKey({ vendorId }),
          })
        }}
      />
    </div>
  )
}
